/**
 * Browser Notifications Hook
 * - Tab title badge with unread count
 * - Native OS notification popups (requires permission)
 * - Notification sound toggle
 * - Multi-tab deduplication via BroadcastChannel
 * - Click-to-focus navigation
 */
import { useEffect, useRef, useCallback, useState } from 'react';
import { API } from '@/App';
import axios from 'axios';

const NOTIFICATION_SOUND_URL = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbsGczHjmP0teleU44RKHL4axnLRs7n9DkpVQjNajW56lJGEG029muMyQxrOHpsig3QNLkqCQpNbjh76gQJUbX5awGKDa76O2hDC1L2eKoEyU3wOHsnBQ1UdjdnBs5TdbXlS';

const APP_TITLE = 'SDR CRM | intalk.io';
const CHANNEL_NAME = 'sdr-crm-notifications';

export function useBrowserNotifications() {
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return localStorage.getItem('notif_sound') !== 'off';
  });
  const [unreadCount, setUnreadCount] = useState(0);

  const seenIdsRef = useRef(new Set());
  const channelRef = useRef(null);
  const audioRef = useRef(null);
  const originalTitle = useRef(APP_TITLE);
  const isLeaderRef = useRef(false);

  // Initialize BroadcastChannel for multi-tab dedup
  useEffect(() => {
    let leaderTimer = null;
    let cancelled = false;
    try {
      channelRef.current = new BroadcastChannel(CHANNEL_NAME);

      // Leader election: first tab to respond is leader
      const electLeader = () => {
        if (cancelled || !channelRef.current) return;
        try {
          isLeaderRef.current = true;
          channelRef.current.postMessage({ type: 'leader_elected' });
        } catch {
          // Channel was closed mid-election (e.g. React StrictMode unmount).
          // Safe to ignore — next mount will re-elect.
        }
      };

      channelRef.current.onmessage = (e) => {
        if (e.data.type === 'leader_elected') {
          isLeaderRef.current = false;
        }
        if (e.data.type === 'notification_shown') {
          seenIdsRef.current.add(e.data.id);
        }
        if (e.data.type === 'unread_count') {
          setUnreadCount(e.data.count);
          updateTabTitle(e.data.count);
        }
      };

      // Claim leadership after short delay
      leaderTimer = setTimeout(electLeader, Math.random() * 500);

      return () => {
        cancelled = true;
        if (leaderTimer) clearTimeout(leaderTimer);
        try { channelRef.current?.close(); } catch { /* ignore */ }
        channelRef.current = null;
      };
    } catch {
      // BroadcastChannel not supported — single tab mode
      isLeaderRef.current = true;
    }
  }, []);

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio(NOTIFICATION_SOUND_URL);
    audioRef.current.volume = 0.3;
  }, []);

  // Update tab title with unread count
  const updateTabTitle = useCallback((count) => {
    if (count > 0) {
      document.title = `(${count > 99 ? '99+' : count}) ${originalTitle.current}`;
    } else {
      document.title = originalTitle.current;
    }
  }, []);

  // Request permission
  const requestPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') return 'denied';
    if (Notification.permission === 'granted') {
      setPermission('granted');
      return 'granted';
    }
    if (Notification.permission !== 'denied') {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result;
    }
    return Notification.permission;
  }, []);

  // Show a browser notification
  const showNotification = useCallback((title, body, data = {}) => {
    if (permission !== 'granted') return;
    if (document.hasFocus()) return; // Don't show if CRM is focused

    const notifId = data.id || `${title}-${Date.now()}`;

    // Multi-tab dedup check
    if (seenIdsRef.current.has(notifId)) return;
    seenIdsRef.current.add(notifId);

    // Only the leader tab shows the notification
    if (!isLeaderRef.current) return;

    // Broadcast that we're showing this notification
    try {
      channelRef.current?.postMessage({ type: 'notification_shown', id: notifId });
    } catch { /* ignore */ }

    try {
      const notification = new Notification(title, {
        body: body?.substring(0, 200),
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: notifId,
        requireInteraction: false,
        silent: !soundEnabled,
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
        // Navigate based on notification type
        if (data.entity_type === 'lead' || data.type === 'lead_assigned') {
          window.location.hash = '';
          window.location.pathname = '/leads';
        } else if (data.entity_type === 'opportunity') {
          window.location.pathname = '/opportunities';
        } else if (data.entity_type === 'import_batch' || data.type === 'import_leads_assigned') {
          window.location.pathname = '/leads';
        } else if (data.type === 'task_created') {
          window.location.pathname = '/tasks';
        }
      };

      // Auto-close after 8 seconds
      setTimeout(() => notification.close(), 8000);
    } catch (err) {
      console.error('Browser notification error:', err);
    }

    // Play sound
    if (soundEnabled) {
      try {
        audioRef.current?.play().catch(() => {});
      } catch { /* ignore */ }
    }
  }, [permission, soundEnabled]);

  // Poll for new notifications and trigger browser popups
  const checkNewNotifications = useCallback(async (token) => {
    if (!token) return 0;
    try {
      const response = await axios.get(`${API}/notifications?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const notifications = response.data || [];
      const unread = notifications.filter(n => !n.read);
      const newCount = unread.length;

      setUnreadCount(newCount);
      updateTabTitle(newCount);

      // Broadcast count to other tabs
      try {
        channelRef.current?.postMessage({ type: 'unread_count', count: newCount });
      } catch { /* ignore */ }

      // Show browser notifications for truly new items
      for (const notif of unread) {
        const nid = notif.id || notif._id;
        if (!seenIdsRef.current.has(nid)) {
          const title = notif.title || notif.change_type || 'New Notification';
          const body = notif.message || notif.entity_name || '';
          showNotification(`[SDR CRM] ${title}`, body, {
            id: nid,
            type: notif.type,
            entity_type: notif.entity_type,
          });
        }
      }

      return newCount;
    } catch {
      return 0;
    }
  }, [showNotification, updateTabTitle]);

  // Toggle sound
  const toggleSound = useCallback(() => {
    setSoundEnabled(prev => {
      const next = !prev;
      localStorage.setItem('notif_sound', next ? 'on' : 'off');
      return next;
    });
  }, []);

  // Clean up tab title on unmount
  useEffect(() => {
    return () => { document.title = originalTitle.current; };
  }, []);

  return {
    permission,
    requestPermission,
    showNotification,
    checkNewNotifications,
    unreadCount,
    soundEnabled,
    toggleSound,
  };
}
